import { Application as PIXIApplication, ApplicationOptions } from 'pixi.js';
import { COLORS } from '../config/constants';

export interface GameApplicationOptions extends Partial<ApplicationOptions> {
  width?: number;
  height?: number;
  backgroundColor?: number;
}

/**
 * Game application wrapper around PIXI.Application.
 * Handles initialization, resize, and provides access to core systems.
 */
export class Application {
  public readonly app: PIXIApplication;
  private _initialized: boolean = false;

  constructor(options: GameApplicationOptions = {}) {
    this.app = new PIXIApplication();

    // Store options for async init
    (this as { _initOptions?: GameApplicationOptions })._initOptions = {
      width: options.width ?? window.innerWidth,
      height: options.height ?? window.innerHeight,
      backgroundColor: options.backgroundColor ?? COLORS.BACKGROUND,
      resolution: options.resolution ?? window.devicePixelRatio,
      autoDensity: options.autoDensity ?? true,
      antialias: options.antialias ?? false,
      ...options,
    };
  }

  /**
   * Initialize the application asynchronously
   */
  async init(): Promise<void> {
    if (this._initialized) return;

    const options = (this as { _initOptions?: GameApplicationOptions })._initOptions ?? {};

    await this.app.init({
      width: options.width,
      height: options.height,
      backgroundColor: options.backgroundColor,
      resolution: options.resolution,
      autoDensity: options.autoDensity,
      antialias: options.antialias,
      preference: 'webgl',
    });

    this._initialized = true;
  }

  get initialized(): boolean {
    return this._initialized;
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas as HTMLCanvasElement;
  }

  get stage() {
    return this.app.stage;
  }

  get renderer() {
    return this.app.renderer;
  }

  get ticker() {
    return this.app.ticker;
  }

  get screen() {
    return this.app.screen;
  }

  /**
   * Resize the application
   */
  resize(width: number, height: number): void {
    this.app.renderer.resize(width, height);
  }

  /**
   * Destroy the application
   */
  destroy(): void {
    this.app.destroy(true, { children: true, texture: true });
  }
}
